/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { CartLine, Address, FilterDeliveryModeOption } from '@msdyn365-commerce/retail-proxy';

import groupBy from 'lodash/groupBy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { DeliveryOptionsCartLineAddress } from './utilities/delivery-options-cartLine-address';

/**
 * Input class for delivery options for items in cart.
 */
export class GetDeliveryOptionsForCartLineAddressInput implements IActionInput {
    public deliveryModeOption: FilterDeliveryModeOption;
    public cartLines: CartLine[];

    public constructor(deliveryModeOption: FilterDeliveryModeOption, cartLines: CartLine[]) {
        this.deliveryModeOption = deliveryModeOption;
        this.cartLines = cartLines;
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => 'DeliveryOptionsForCartLineAddress';

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheObjectType = (): string => 'DeliveryOptionsForCartLineAddress';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the CreateProductList method.
 * @param inputData - The input data passed to the createInput method.
 * @param cartLines - The list of cart line.
 * @returns - The Action input.
 */
export const createDeliveryOptionsForCartLineAddressInput = (
    inputData: ICreateActionContext,
    deliveryModeOption: FilterDeliveryModeOption,
    cartLines: CartLine[]
): GetDeliveryOptionsForCartLineAddressInput => {
    console.log('CreateDeliveryOptionsForCartLineAddressInput', deliveryModeOption, cartLines, inputData);
    return new GetDeliveryOptionsForCartLineAddressInput(deliveryModeOption, cartLines);
};

/**
 * Calls the Retail API to get the delivery options for items in the cart.
 * @param input
 * @param ctx
 */
export async function getDeliveryOptionsForCartLineAddress(
    input: GetDeliveryOptionsForCartLineAddressInput,
    ctx: IActionContext
): Promise<DeliveryOptionsCartLineAddress[]> {
    // If no input is provided fail out
    if (!input) {
        throw new Error('[getDeliveryOptionsForCartLineAddress]No valid Input was provided, failing');
    }
    const checkoutState = await getCheckoutState(ctx);
    const cart = checkoutState.checkoutCart.cart;

    if (cart && cart.CartLines && cart.CartLines.length === 0) {
        ctx.trace('[getDeliveryOptionsForCartLineItems] Not able to get cart');
        console.log('[getDeliveryOptionsForCartLineItems] Not able to get cart or cartlines are empty');
        return <DeliveryOptionsCartLineAddress[]>{};
    }
    const cartLinesForShipping = input.cartLines;
    if (cartLinesForShipping.length > 0) {
        const getGroupByDelivery = (items: CartLine[]) => groupBy(items, item => item.ShippingAddress?.RecordId);
        const groupDelivery = getGroupByDelivery(cartLinesForShipping);
        // const deliveryOptions: DeliveryOptionsCartLineAddress[] = [];
        console.log('Group Delivery', groupDelivery);
        const deliveryOptionsPromiseList = Object.keys(groupDelivery).map(async key => {
            // eslint-disable-next-line security/detect-object-injection
            const cartLines = groupDelivery[key];
            const cartLineIds = cartLines.map(cartLine => (cartLine.LineId !== undefined ? cartLine.LineId : ''));
            console.log('Cart Line IDs', cartLineIds);
            if (cartLines[0].ShippingAddress === undefined && cart.ShippingAddress === undefined) {
                return _getDeliveryOption(cart.Id, input.deliveryModeOption, false, ctx, checkoutState.shippingAddress, cartLineIds);
            } else if (cart.ShippingAddress !== undefined) {
                return _getDeliveryOption(cart.Id, input.deliveryModeOption, false, ctx, cart.ShippingAddress, cartLineIds);
            } else {
                return _getDeliveryOption(cart.Id, input.deliveryModeOption, false, ctx, cartLines[0].ShippingAddress, cartLineIds);
            }
        });
        console.log('Delivery Options Promise List', deliveryOptionsPromiseList);
        return Promise.all(deliveryOptionsPromiseList);
    }
    return <DeliveryOptionsCartLineAddress[]>{};
}

/**
 * Calls the Retail API to get the delivery options for items in the cart.
 * @param shippingAddress
 * @param cartLinesId
 * @param deliveryOptionFilter
 * @param ignoreLinesWithNoDeliveryOptions
 * @param ctx
 */
async function _getDeliveryOption(
    cartId: string,
    deliveryOptionFilter: number,
    ignoreLinesWithNoDeliveryOptions: boolean,
    ctx: IActionContext,
    shippingAddress?: Address,
    cartLinesId?: string[]
): Promise<DeliveryOptionsCartLineAddress> {
    if (!shippingAddress || shippingAddress === undefined) {
        ctx.trace('[_getDeliveryOption] Not able to get delivery options for cart line');
        console.log('[_getDeliveryOption] Not able to get address for cart line');
        return <DeliveryOptionsCartLineAddress>{};
    }
    return getDeliveryOptionsAsync(
        { callerContext: ctx },
        cartId,
        shippingAddress,
        cartLinesId,
        deliveryOptionFilter,
        ignoreLinesWithNoDeliveryOptions
    )
        .then(result => {
            if (result && result.length > 0) {
                const deliveryOptions = new DeliveryOptionsCartLineAddress(result, false, undefined, cartLinesId);
                return deliveryOptions;
            } else {
                const deliveryOptions = new DeliveryOptionsCartLineAddress([], true, 'No delivery options available', cartLinesId);
                console.log('[_getDeliveryOption] No delivery options available');
                return deliveryOptions;
            }
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getDeliveryOptionsForCartLineItems]Error executing action');
            const deliveryOptions = new DeliveryOptionsCartLineAddress([], true, error.message, cartLinesId);
            console.log('[getDeliveryOptionsForCartLineItems]Error executing action');
            return deliveryOptions;
        });
}

export const getDeliveryOptionsForCartLineAddressDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-delivery-options-for-cart-line-address',
    action: getDeliveryOptionsForCartLineAddress as IAction<DeliveryOptionsCartLineAddress[]>,
    input: createDeliveryOptionsForCartLineAddressInput
});

export default getDeliveryOptionsForCartLineAddressDataAction;
